import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search, Filter, Download, Wallet, ArrowUpDown, X, Loader2,
  FileText, ExternalLink, ChevronsUpDown, Check, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const eur = (n) => (n == null ? "—" : `${Number(n).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const EDITOR_ROLES = new Set(["agente_facturacion", "contable", "administracion", "socio_director", "dir_admin_general"]);

const ESTADOS_FACT = [
  { v: "no_solicitada", l: "No solicitada" },
  { v: "solicitada", l: "Solicitada · no recibida" },
  { v: "recibida", l: "Recibida" },
  { v: "mensual", l: "Mensual" },
  { v: "no_aplica", l: "No aplica" },
];
const ESTADOS_PAGO = [
  { v: "sin_pagar", l: "Sin pagar", cls: "bg-red-50 text-red-700 border-red-200" },
  { v: "parcial", l: "Parcial", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { v: "pagado", l: "Pagado", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
];
const MEDIOS = ["Transferencia", "Tarjeta", "Domiciliación", "PayPal", "Efectivo", "Compensación", "Otro"];

function ProviderCombo({ providers, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const sel = providers.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between font-normal text-xs" data-testid={testid}>
          <span className="truncate">{sel ? sel.name : "Todos los proveedores"}</span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Escribe el nombre…" />
          <CommandList>
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup>
              <CommandItem value="all" onSelect={() => { onChange(""); setOpen(false); }}>
                <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />Todos
              </CommandItem>
              {providers.map((p) => (
                <CommandItem key={p.id} value={p.name} onSelect={() => { onChange(p.id); setOpen(false); }}>
                  <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100" : "opacity-0"}`} />{p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RegistrarPagoDialog({ row, onClose, onSaved, canEdit }) {
  const today = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(today);
  const [cantidad, setCantidad] = useState(row.pendiente > 0 ? String(row.pendiente) : "");
  const [medio, setMedio] = useState(MEDIOS[0]);
  const [notas, setNotas] = useState("");
  const [tipo, setTipo] = useState("pago");
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/solicitud-factura/reserva/${row.id}/registrar-pago`, {
      fecha, cantidad: parseFloat(cantidad), medio, notas: notas || null, tipo,
    })).data,
    onSuccess: (d) => {
      toast.success(`${tipo === "pago" ? "Pago" : "Devolución"} registrado · Cashflow actualizado`);
      onSaved(d);
    },
    onError: (e) => toast.error(apiErrorMessage(e, "No se pudo registrar el pago")),
  });
  const valid = fecha && cantidad && parseFloat(cantidad) > 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reg-pago-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-600" />Registrar {tipo === "pago" ? "pago" : "devolución"}</DialogTitle>
          <DialogDescription className="text-xs">
            <strong>{row.provider_name}</strong> · {row.service} · {row.client_name}<br />
            Total a pagar: <strong className="tabular-nums">{eur(row.total_a_pagar)}</strong> · Ya pagado: <strong className="tabular-nums">{eur(row.pagado)}</strong> · <span className="text-amber-700">Pendiente: <strong className="tabular-nums">{eur(row.pendiente)}</strong></span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex overflow-hidden rounded-lg border" data-testid="reg-pago-tipo">
            <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${tipo === "pago" ? "bg-emerald-600 text-white" : "bg-card hover:bg-muted"}`} onClick={() => setTipo("pago")} data-testid="reg-pago-tipo-pago">Pago</button>
            <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${tipo === "devolucion" ? "bg-amber-600 text-white" : "bg-card hover:bg-muted"}`} onClick={() => setTipo("devolucion")} data-testid="reg-pago-tipo-devolucion">Devolución</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Fecha</Label><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="reg-pago-fecha" /></div>
            <div className="space-y-1.5">
              <Label className="text-xs">Importe (€)</Label>
              <div className="flex gap-1">
                <Input type="number" step="0.01" value={cantidad} onChange={(e) => setCantidad(e.target.value)} data-testid="reg-pago-cantidad" />
                {tipo === "pago" && row.pendiente > 0 && (
                  <Button variant="outline" size="sm" className="h-9 shrink-0 text-[10px]" onClick={() => setCantidad(String(row.pendiente))} title="Rellenar con el pendiente" data-testid="reg-pago-full">Full</Button>
                )}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Método de pago</Label>
            <Select value={medio} onValueChange={setMedio}>
              <SelectTrigger data-testid="reg-pago-medio"><SelectValue /></SelectTrigger>
              <SelectContent>{MEDIOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Notas (opcional)</Label>
            <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Ref. bancaria, comentario, etc." data-testid="reg-pago-notas" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!valid || mut.isPending || !canEdit} data-testid="reg-pago-save">
            {mut.isPending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Guardando…</> : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TodasTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);
  const [q, setQ] = useState("");
  const [providerId, setProviderId] = useState("");
  const [agent, setAgent] = useState("");
  const [country, setCountry] = useState("");
  const [estadoFact, setEstadoFact] = useState("");
  const [estadoPago, setEstadoPago] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [withInvoice, setWithInvoice] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [sortKey, setSortKey] = useState("date_entry");
  const [sortAsc, setSortAsc] = useState(false);

  const params = useMemo(() => ({
    q, provider_id: providerId, agent, country, estado_fact: estadoFact, estado_pago: estadoPago,
    date_from: dateFrom, date_to: dateTo, with_invoice_only: withInvoice,
  }), [q, providerId, agent, country, estadoFact, estadoPago, dateFrom, dateTo, withInvoice]);

  const listQ = useQuery({
    queryKey: ["facturas-todas", params],
    queryFn: async () => (await api.get("/solicitud-factura/todas", { params })).data,
    keepPreviousData: true,
  });

  const rows = useMemo(() => {
    const src = listQ.data?.rows || [];
    return [...src].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (typeof av === "number" && typeof bv === "number") return sortAsc ? av - bv : bv - av;
      return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [listQ.data, sortKey, sortAsc]);
  const summary = listQ.data?.summary || { count: 0, total_facturado: 0, total_pagado: 0, total_pendiente: 0, n_facturas: 0 };
  const providers = listQ.data?.providers || [];
  const agents = listQ.data?.agents || [];
  const countries = listQ.data?.countries || [];

  const resetFilters = () => {
    setQ(""); setProviderId(""); setAgent(""); setCountry(""); setEstadoFact("");
    setEstadoPago(""); setDateFrom(""); setDateTo(""); setWithInvoice(false);
  };

  const sortBy = (k) => { if (sortKey === k) setSortAsc(!sortAsc); else { setSortKey(k); setSortAsc(false); } };

  const doExport = async () => {
    try {
      const res = await api.get("/solicitud-factura/todas/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `facturas_proveedor_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch (e) { toast.error(apiErrorMessage(e, "No se pudo exportar")); }
  };

  const openPDF = (row) => {
    const f = row.facturas?.[0];
    if (f?.s3_url) window.open(f.s3_url, "_blank");
    else if (f?.file_id) window.open(`${api.defaults.baseURL}/gridfs/${f.file_id}`, "_blank");
    else toast.info("Esta reserva no tiene PDF de factura vinculado");
  };

  const hasFilters = q || providerId || agent || country || estadoFact || estadoPago || dateFrom || dateTo || withInvoice;

  return (
    <div className="space-y-4" data-testid="todas-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Reservas</p><p className="text-lg font-bold tabular-nums" data-testid="todas-kpi-count">{summary.count.toLocaleString("es-ES")}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Facturas PDF</p><p className="text-lg font-bold tabular-nums" data-testid="todas-kpi-nfact">{summary.n_facturas.toLocaleString("es-ES")}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground">Total a pagar</p><p className="text-lg font-bold tabular-nums" data-testid="todas-kpi-total">{eur(summary.total_facturado)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground text-emerald-700">Pagado</p><p className="text-lg font-bold tabular-nums text-emerald-700" data-testid="todas-kpi-pagado">{eur(summary.total_pagado)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-[10px] uppercase text-muted-foreground text-amber-700">Pendiente</p><p className="text-lg font-bold tabular-nums text-amber-700" data-testid="todas-kpi-pendiente">{eur(summary.total_pendiente)}</p></CardContent></Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="space-y-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente, servicio, proveedor, nº factura…" className="pl-8" data-testid="todas-search" />
            </div>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[140px]" data-testid="todas-date-from" />
              <span className="text-xs text-muted-foreground">→</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[140px]" data-testid="todas-date-to" />
            </div>
            <Button variant="outline" size="sm" onClick={doExport} className="ml-auto" data-testid="todas-export">
              <Download className="mr-1.5 h-3.5 w-3.5" />Excel
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            <div className="min-w-[140px]"><ProviderCombo providers={providers} value={providerId} onChange={setProviderId} testid="todas-flt-provider" /></div>
            <Select value={agent || "all"} onValueChange={(v) => setAgent(v === "all" ? "" : v)}>
              <SelectTrigger className="text-xs" data-testid="todas-flt-agent"><SelectValue placeholder="Agente" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los agentes</SelectItem>{agents.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={country || "all"} onValueChange={(v) => setCountry(v === "all" ? "" : v)}>
              <SelectTrigger className="text-xs" data-testid="todas-flt-country"><SelectValue placeholder="País" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los países</SelectItem>{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={estadoFact || "all"} onValueChange={(v) => setEstadoFact(v === "all" ? "" : v)}>
              <SelectTrigger className="text-xs" data-testid="todas-flt-estado-fact"><SelectValue placeholder="Estado factura" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas las facturas</SelectItem>{ESTADOS_FACT.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={estadoPago || "all"} onValueChange={(v) => setEstadoPago(v === "all" ? "" : v)}>
              <SelectTrigger className="text-xs" data-testid="todas-flt-estado-pago"><SelectValue placeholder="Estado pago" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los pagos</SelectItem>{ESTADOS_PAGO.map((e) => <SelectItem key={e.v} value={e.v}>{e.l}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={withInvoice} onChange={(e) => setWithInvoice(e.target.checked)} data-testid="todas-only-invoice" />Con PDF
              </label>
              {hasFilters && <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto text-[10px]" data-testid="todas-reset"><RotateCcw className="mr-1 h-3 w-3" />Limpiar</Button>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <div className="rounded-lg border" data-testid="todas-table">
        <div className="max-h-[calc(100vh-350px)] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-zinc-50 text-[10px] uppercase text-muted-foreground shadow-sm">
              <tr>
                <th className="cursor-pointer px-3 py-2" onClick={() => sortBy("date_entry")}><span className="inline-flex items-center gap-1">Fecha <ArrowUpDown className="h-3 w-3" /></span></th>
                <th className="px-3 py-2">Cliente / Viaje</th>
                <th className="px-3 py-2">Agente</th>
                <th className="cursor-pointer px-3 py-2" onClick={() => sortBy("provider_name")}>Proveedor</th>
                <th className="px-3 py-2">Servicio · Ciudad</th>
                <th className="cursor-pointer px-3 py-2 text-right" onClick={() => sortBy("total_a_pagar")}>Total</th>
                <th className="cursor-pointer px-3 py-2 text-right" onClick={() => sortBy("pagado")}>Pagado</th>
                <th className="cursor-pointer px-3 py-2 text-right" onClick={() => sortBy("pendiente")}>Pendiente</th>
                <th className="px-3 py-2 text-center">Factura</th>
                <th className="px-3 py-2 text-center">Pago</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {listQ.isLoading ? (
                <tr><td colSpan={11} className="px-3 py-6"><Skeleton className="h-40 w-full" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="todas-empty">Sin resultados con estos filtros.</td></tr>
              ) : (
                rows.map((r) => {
                  const p = ESTADOS_PAGO.find((e) => e.v === r.estado_pago);
                  return (
                    <tr key={r.id} className="border-t hover:bg-zinc-50/60" data-testid={`todas-row-${r.id}`}>
                      <td className="px-3 py-1.5 text-xs tabular-nums">{r.date_entry || "—"}</td>
                      <td className="px-3 py-1.5 max-w-[180px] truncate" title={r.client_name}>{r.client_name || "—"}<br /><a href={`/app/trips/${r.trip_id}`} className="text-[10px] text-muted-foreground hover:underline">{r.trip_id?.slice(0, 8)}</a></td>
                      <td className="px-3 py-1.5 text-xs">{r.agent || "—"}</td>
                      <td className="px-3 py-1.5 max-w-[160px] truncate font-medium" title={r.provider_name}>{r.provider_name || "—"}<br /><span className="text-[10px] text-muted-foreground">{r.country}</span></td>
                      <td className="px-3 py-1.5 max-w-[180px] truncate text-xs" title={r.service}>{r.service}{r.city ? <><br /><span className="text-[10px] text-muted-foreground">{r.city}</span></> : null}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{eur(r.total_a_pagar)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{eur(r.pagado)}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${r.pendiente > 0.01 ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>{eur(r.pendiente)}</td>
                      <td className="px-3 py-1.5 text-center">
                        <Badge variant="outline" className="text-[9px]">{ESTADOS_FACT.find((e) => e.v === r.estado_fact)?.l || r.estado_fact}</Badge>
                        {r.n_facturas > 0 && <div className="mt-0.5 text-[10px] text-muted-foreground">{r.n_facturas} PDF</div>}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Badge variant="outline" className={`text-[9px] ${p?.cls || ""}`}>{p?.l || r.estado_pago}</Badge>
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center justify-end gap-1">
                          {r.n_facturas > 0 && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver PDF factura" onClick={() => openPDF(r)} data-testid={`todas-pdf-${r.id}`}>
                              <FileText className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canEdit && r.estado_pago !== "pagado" && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-700" title="Registrar pago" onClick={() => setPayRow(r)} data-testid={`todas-pagar-${r.id}`}>
                              <Wallet className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Ir a la reserva" onClick={() => window.open(`/app/trips/${r.trip_id}/reservas/${r.id}`, "_blank")} data-testid={`todas-open-${r.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {payRow && (
        <RegistrarPagoDialog row={payRow} canEdit={canEdit}
          onClose={() => setPayRow(null)}
          onSaved={() => { setPayRow(null); qc.invalidateQueries({ queryKey: ["facturas-todas"] }); }} />
      )}
    </div>
  );
}
